import type { EmployeeDesignation, UserRole } from '../types';
import { hasFullCrmEmployeeAccess, hasOfficeOpsPrivileges, hasSalesCrmAccess } from './employeeDesignations';

/** Signed-in CRM users who may open the full People hub (benefits, travel, time-off requests). */
export function isPeopleHubUserRole(
  authRole: string | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  return hasFullCrmEmployeeAccess({
    authRole: authRole as UserRole | undefined,
    designations,
  });
}

/** Field staff and scoped W-2 employees — limited People nav. */
export function isFieldStaffPeopleNavRole(authRole: string | undefined): boolean {
  return (
    authRole === 'staff'
    || authRole === 'employee_shop'
    || authRole === 'employee_office'
  );
}

/** May see the People heading and any allowed People sub-routes. */
export function canAccessPeopleHubNav(
  authRole: string | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  return isPeopleHubUserRole(authRole, designations) || isFieldStaffPeopleNavRole(authRole);
}

/** May open People → Hiring (job postings and applications). */
export function canAccessPeopleHiring(
  authRole: string | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  return isPeopleHubUserRole(authRole, designations);
}

/** People → Team directory (admin + Employee — Office). */
export function canAccessPeopleTeam(authRole: string | undefined): boolean {
  return authRole === 'admin' || authRole === 'employee_office';
}

/** Field staff may not open benefits or travel even via direct URL. */
export function canAccessPeopleBenefitsOrTravel(
  authRole: string | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  return (
    isPeopleHubUserRole(authRole, designations)
    || hasSalesCrmAccess({ authRole, designations })
  );
}

/** May submit or view personal time-off requests on the team calendar page. */
export function canRequestPeopleHubTimeOff(
  authRole: string | undefined,
  designations?: EmployeeDesignation[] | string[],
): boolean {
  // W-2 office (and shop) employees request PTO like full employees; do not
  // require office_manager / sales_rep. 1099 `staff` stays excluded.
  if (authRole === 'employee_office' || authRole === 'employee_shop') return true;
  return (
    isPeopleHubUserRole(authRole, designations)
    || hasSalesCrmAccess({ authRole, designations })
  );
}

/**
 * Admins, executive assistants, and office managers may view all employees’
 * leave balance data and the full time-off approval queue.
 */
export function canViewOrgWideEmployeeBenefits(args: {
  authRole: string | undefined;
  designations: EmployeeDesignation[] | string[] | undefined;
}): boolean {
  if (args.authRole === 'admin') return true;
  return hasOfficeOpsPrivileges(args.designations);
}

/** Same access as `canViewOrgWideEmployeeBenefits` — matches Firestore time-off approval rules. */
export function canApproveTimeOffRequests(args: {
  authRole: string | undefined;
  designations: EmployeeDesignation[] | string[] | undefined;
}): boolean {
  return canViewOrgWideEmployeeBenefits(args);
}
